import { Task } from "@/core/types";
import { useDeleteTaskMutation } from "@/hooks/tasks";

import { useModal } from "../modals/useModal";

export type TaskDeleteAction = {
  label: string;
  disabled: boolean;
  tooltip?: string;
  isPending: boolean;
  onClick: () => void;
};

/**
 * The destructive action offered for a task, shared by the detail sidebar and
 * the task card context menu so the two never disagree.
 *
 * A completed setting task offers "Strip Route" instead of "Delete": deleting
 * would throw away the record of a route that is live in the gym, so the only
 * way forward from Done is to strip it. A completed stripping task offers
 * nothing — restore the route first by moving the task out of Done.
 */
export const useTaskDeleteAction = (
  task?: Task,
  { onComplete }: { onComplete?: () => void } = {},
): TaskDeleteAction => {
  const { display: displayModal } = useModal();
  const deleteMutation = useDeleteTaskMutation(task?.venue);

  const onDelete = () => {
    if (!task) {
      return;
    }
    deleteMutation.mutateAsync(task.uuid).then(() => onComplete?.());
  };

  const base = {
    label: "Delete",
    disabled: !task,
    isPending: deleteMutation.isPending,
    onClick: onDelete,
  };

  if (!task || task.status !== "DONE") {
    return base;
  }

  if (task.type === "SET") {
    if (task.route?.endTime) {
      return {
        ...base,
        label: "Strip Route",
        disabled: true,
        tooltip: "This route has already been stripped",
        onClick: () => {},
      };
    }
    return {
      ...base,
      label: "Strip Route",
      onClick: () => displayModal("CONFIRM_STRIP_ROUTE", { task, onComplete }),
    };
  }

  if (task.type === "STRIP") {
    return {
      ...base,
      disabled: true,
      tooltip: "Move this task out of Done to delete it",
      onClick: () => {},
    };
  }

  return base;
};
